import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Workflow, Network, Activity, ShieldCheck, MessageCircle } from 'lucide-react';
import BrandLogoImage from '../../branding/BrandLogoImage';
import { identityApi } from '../../api/client';
import { useSessionStore } from '../../session/store';
import { useBranding } from '../../branding/portalBrandingState';
import {
  NEUTRAL_AUTH_HEADLINE,
  NEUTRAL_AUTH_SUBHEAD,
  resolveAuthPresentation,
} from '../../branding/brandingPresentation';
import { ROUTES } from '../../routes/constants';

const WHATSAPP_URL = 'https://wa.me/919903862980?text=I%20need%20to%20know%20more%20about%20ALGOSYS';

// One-time animated brand line (above the headline). Subtle fade + slight upward movement.
const BRAND_PHRASES = ['A for Algo.', 'A for AlgoSys.', 'AlgoSys — Cloud for Trading Automation'];
const BRAND_FINAL = BRAND_PHRASES.length - 1;

const LG_QUERY = '(min-width: 1024px)';
const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';
const SIGNUP_VERIFY_MESSAGE = 'Verify your email before login. Check your inbox for a verification email.';
const UNVERIFIED_LOGIN_MESSAGE = 'Email unverified, check your mail.';
const RESEND_SUCCESS_MESSAGE = 'If this email needs verification, we’ve sent a new verification link.';

// Animate the brand line only at the desktop (lg) two-column breakpoint AND when the user has not
// requested reduced motion. Below lg, or with reduced motion, show the final static line. Read
// synchronously so the initial state matches the viewport (no first-paint flash). Guarded so it is
// safe where `window` is unavailable (SSR / prerender / tests).
const canAnimateBrand = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(LG_QUERY).matches &&
  !window.matchMedia(REDUCE_QUERY).matches;

// Confirmed product capabilities only — no claims about profit, returns, loss reduction,
// investment advice, performance, risk-free trading, regulation, certifications or speed.
const BENEFITS = [
  {
    icon: Workflow,
    title: 'Automate TradingView alerts',
    desc: 'Your Pine Script alert becomes a live order on Zerodha or Fyers automatically.',
  },
  {
    icon: Network,
    title: 'One platform, all your brokers',
    desc: 'Connect Zerodha, Fyers, Angel One, Mastertrust, Kotak Neo, Nuvama and more from one workspace.',
  },
  {
    icon: Activity,
    title: 'Test before you go live',
    desc: "Run your strategy in paper mode first. Switch to live execution when you're confident.",
  },
  {
    icon: ShieldCheck,
    title: 'Full visibility, full control',
    desc: 'Every signal received, every order placed, and every position tracked in real time.',
  },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSession = useSessionStore((s) => s.setSession);
  const presentation = resolveAuthPresentation(useBranding());

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]       = useState('');
  const [notice, setNotice]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [resending, setResending] = useState(false);

  // Login-specific browser/tab title; restore the previous title on unmount so other routes are
  // unaffected. Guarded for SSR/prerender safety. No metadata library used. The title follows the
  // resolved portal so a branded portal never shows the AlgoSys-specific title, not even while the
  // branding request is still pending.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const previousTitle = document.title;
    document.title = presentation.documentTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [presentation.documentTitle]);

  useEffect(() => {
    if (searchParams.get('verifyEmail') === '1') {
      setNotice(SIGNUP_VERIFY_MESSAGE);
    }
  }, [searchParams]);

  // Animated brand line. Lazy-init from the current viewport so there is no first-paint flash.
  const [brandAnimate, setBrandAnimate] = useState(canAnimateBrand);
  const [brandStep, setBrandStep] = useState(() => (canAnimateBrand() ? 0 : BRAND_FINAL));

  // Re-evaluate when the viewport crosses lg or the reduced-motion preference changes (so it works
  // live during devtools responsive testing). Below lg / reduced motion → final static line, no
  // timers; at lg without reduced motion → run the sequence once.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mqlLg = window.matchMedia(LG_QUERY);
    const mqlReduce = window.matchMedia(REDUCE_QUERY);
    let timers: number[] = [];
    const clear = () => {
      timers.forEach((t) => window.clearTimeout(t));
      timers = [];
    };
    const run = () => {
      clear();
      if (mqlReduce.matches || !mqlLg.matches) {
        setBrandAnimate(false);
        setBrandStep(BRAND_FINAL);
        return;
      }
      setBrandAnimate(true);
      setBrandStep(0);
      timers.push(window.setTimeout(() => setBrandStep(1), 1000));
      timers.push(window.setTimeout(() => setBrandStep(2), 2000));
    };
    run();
    mqlLg.addEventListener('change', run);
    mqlReduce.addEventListener('change', run);
    return () => {
      clear();
      mqlLg.removeEventListener('change', run);
      mqlReduce.removeEventListener('change', run);
    };
  }, []);

  const feedback = error || notice;
  const feedbackTone = error ? 'error' : 'notice';
  const canResendVerification = feedback === SIGNUP_VERIFY_MESSAGE || feedback === UNVERIFIED_LOGIN_MESSAGE;
  const feedbackClassName = feedbackTone === 'error'
    ? 'rounded-lg border border-bear/30 bg-bear/10 px-3 py-2 text-xs text-bear'
    : 'rounded-lg border border-amber/30 bg-amber/10 px-3 py-2 text-xs text-amber';

  const isUnverifiedLoginError = (message: string) => {
    const normalized = message.toLowerCase();
    return normalized.includes('email unverified') || normalized.includes('verify your email');
  };

  const handleResendVerification = async () => {
    if (!email) { setError('Enter your email first.'); return; }
    setResending(true);
    try {
      await identityApi.resendVerification(email);
      setError('');
      setNotice(RESEND_SUCCESS_MESSAGE);
    } catch {
      setError('Unable to resend verification. Please try again.');
      setNotice('');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Email and password are required.'); return; }

    setError('');
    setNotice('');
    setLoading(true);
    try {
      const result = await identityApi.login({ email, password });
      const profile = result.user ?? await identityApi.me();
      setSession({
        user: profile,
        token: result.access_token,
        roles: profile.roles,
        mustChangePassword: result.must_change_password,
      });
      const next = result.must_change_password
        ? ROUTES.CHANGE_PASSWORD
        : (searchParams.get('next') || ROUTES.STRATEGIES);
      navigate(next, { replace: true });
    } catch (err: any) {
      const message = err?.message || 'Login failed. Check your credentials and try again.';
      setError(isUnverifiedLoginError(message) ? UNVERIFIED_LOGIN_MESSAGE : message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-font-body relative flex min-h-screen flex-col bg-bg-base text-txt-primary">

      {/* Scoped keyframe for the one-time brand line — no global token / tailwind.config change. */}
      <style>{`
        @keyframes algosysBrandIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .algosys-brand-in { animation: algosysBrandIn 0.6s ease both; }
      `}</style>

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-brand/[0.06] blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-info/[0.05] blur-[100px]" />
        <div className="absolute top-1/2 -left-32 w-[350px] h-[350px] rounded-full bg-bull/[0.03] blur-[80px]" />
      </div>

      {/* Two-column area (desktop) / stacked (mobile). Wrapped so the footer can sit below it. */}
      <div className="relative z-10 flex flex-1 flex-col lg:grid lg:grid-cols-2">

      {/* ───────────────────────── Login form (first in DOM → shown first on mobile) ───────────────────────── */}
      <section className="relative flex items-center justify-center px-4 pt-6 pb-6 sm:px-6 lg:order-2 lg:py-0">
        <div className="relative z-10 w-full max-w-[390px] lg:max-w-[480px] lg:-translate-y-6">
          {/* Compact logo — top of the card on mobile/tablet only. A branded portal with a
              safely loaded custom logo shows the Operator's own mark; otherwise this remains
              the bundled AlgoSys platform logo, labelled as such. */}
          <div className="mb-3 flex items-center gap-3 lg:hidden">
            <BrandLogoImage
              logoSrc={presentation.logoSrc}
              alt={presentation.logoAlt}
              className="h-auto w-24 object-contain"
            />
            {presentation.showBusinessNameBadge && (
              <span className="auth-font-heading min-w-0 truncate text-sm font-semibold text-brand" title={presentation.businessName ?? undefined}>
                {presentation.businessName}
              </span>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            noValidate
            className="relative overflow-hidden rounded-xl border border-bg-border/60 bg-bg-panel p-4 shadow-modal md:p-6 lg:p-9"
          >
            {/* Top brand accent line — consistent with the rest of the app */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/50 to-transparent" />

            <div className="mb-3 space-y-0.5 lg:mb-6 lg:space-y-1.5">
              {/* Intentionally a paragraph, not a heading: the single page heading is the marketing headline. */}
              <p className="auth-font-heading text-3xl font-bold tracking-tight text-txt-primary lg:text-2xl">Welcome Back</p>
              <p className="text-sm text-txt-secondary">
                {/* Shorter on phones/tablets; full subtitle restored at the desktop breakpoint. */}
                <span className="lg:hidden">{presentation.loginSubtitle}</span>
                <span className="hidden lg:inline">Sign in to manage your strategies and trading accounts.</span>
              </p>
            </div>

            <div className="space-y-3 lg:space-y-5">
              {/* Email */}
              <div className="space-y-1.5">
                <label htmlFor="email" className="field-label sr-only lg:not-sr-only">Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  autoComplete="email"
                  className="input-base h-12 lg:h-9"
                  disabled={loading}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <label htmlFor="password" className="field-label sr-only lg:not-sr-only">Password</label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    autoComplete="current-password"
                    className="input-base h-12 pr-10 lg:h-9"
                    disabled={loading}
                    aria-invalid={!!error}
                    aria-describedby={error ? 'login-error' : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-txt-muted transition-colors hover:text-txt-primary disabled:opacity-40"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Error / verification message */}
              {feedback && (
                <div className="space-y-2">
                  <p
                    id="login-error"
                    role="alert"
                    className={feedbackClassName}
                  >
                    {feedback}
                    {canResendVerification && (
                      <>
                        {' '}
                        Didn’t get it?{' '}
                        <button
                          type="button"
                          onClick={handleResendVerification}
                          disabled={resending}
                          className="text-amber underline transition-colors hover:text-amber/80 disabled:opacity-40"
                        >
                          {resending ? 'Resending…' : 'Resend →'}
                        </button>
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Submit */}
              <button type="submit" disabled={loading} className="btn-primary mt-1 h-12 w-full justify-center lg:h-auto">
                {loading ? 'Signing in…' : 'Sign In'}
              </button>

              {/* Forgot / Create account — real application routes */}
              <div className="flex justify-between border-t border-bg-border/40 pt-2.5 text-xs text-txt-muted lg:pt-3">
                <Link to={ROUTES.FORGOT_PASSWORD} className="transition-colors hover:text-brand">Forgot password?</Link>
                <Link to={ROUTES.SIGNUP} className="transition-colors hover:text-brand">Create account</Link>
              </div>
              {/* AlgoSys's own WhatsApp support contact. Shown only once the portal is positively
                  resolved as the AlgoSys portal: the Phase 3A response carries no Operator support
                  contact, so this must never be presented as a branded portal's contact detail. */}
              {presentation.showAlgosysMarketing && (
                <div className="text-center text-xs text-txt-muted">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors hover:text-amber"
                  >
                    Need help? Chat with us →
                  </a>
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* ───────────────────────── Marketing panel (left on desktop, below on mobile) ───────────────────────── */}
      <section
        aria-labelledby="hero-heading"
        className="relative flex flex-col justify-center overflow-hidden border-t border-bg-border/50 bg-bg-surface px-6 pt-6 pb-10 sm:px-10 lg:order-1 lg:border-r lg:border-t-0 lg:px-16 lg:py-0 xl:px-20"
      >
        {/* Green/amber ambient glows + faint grid texture */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-24 top-0 h-[460px] w-[460px] rounded-full bg-[#68B600]/[0.07] blur-[130px]" />
          <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-brand/[0.05] blur-[120px]" />
          <div
            className="absolute inset-0 opacity-[0.22]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(148,163,184,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
          {/* Faint, monochrome ambient sparkline — texture, not a trading chart */}
          <svg
            className="absolute bottom-10 left-0 w-full opacity-[0.06]"
            viewBox="0 0 600 120"
            fill="none"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0 90 L60 78 L120 86 L180 60 L240 70 L300 44 L360 54 L420 30 L480 40 L540 18 L600 26"
              stroke="#68B600"
              strokeWidth="2"
            />
          </svg>
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-md flex-col lg:mx-0 lg:max-w-lg">
          {/* Brand — the Operator's own logo on a branded portal with a safely loaded custom
              mark; otherwise the bundled AlgoSys platform logo. A branded portal shows its own
              business name underneath instead of implying the AlgoSys mark belongs to it. */}
          <div className="mb-8 hidden lg:block">
            <BrandLogoImage
              logoSrc={presentation.logoSrc}
              alt={presentation.logoAlt}
              className="h-[100px] w-auto object-contain"
            />
            {presentation.showBusinessNameBadge && (
              <p
                className="auth-font-heading mt-2 max-w-full truncate text-lg font-semibold text-brand"
                title={presentation.businessName ?? undefined}
              >
                {presentation.businessName}
              </p>
            )}
          </div>

          {/* Animated AlgoSys brand line. Decorative, and AlgoSys-specific, so it is rendered only
              on the resolved AlgoSys portal. The fixed-height clip is kept in every state so
              resolving branding does not shift the layout. */}
          <div
            aria-hidden="true"
            className="mb-3 flex items-center lg:mb-4 lg:h-6 lg:overflow-hidden"
          >
            {presentation.showAlgosysMarketing && (
              <span
                key={brandStep}
                className={`block font-mono text-sm font-medium leading-snug tracking-wide text-[#68B600] break-words lg:truncate lg:leading-normal ${
                  brandAnimate ? 'algosys-brand-in' : ''
                }`}
              >
                {BRAND_PHRASES[brandStep]}
              </span>
            )}
          </div>

          {presentation.showAlgosysMarketing ? (
            <>
              <h1 id="hero-heading" className="auth-font-heading text-[1.75rem] font-bold leading-[1.15] tracking-tight text-txt-primary md:text-[2.25rem] lg:text-4xl lg:leading-tight">
                Your Strategy Runs.{' '}
                <span className="text-[#68B600]">You Don't Have To Watch.</span>
              </h1>

              <p className="mt-4 max-w-md text-sm leading-relaxed text-txt-secondary lg:text-base">
                A cloud platform for Indian traders who want their strategies running automatically — without
                watching the screen all day.
              </p>
            </>
          ) : (
            <>
              <h1 id="hero-heading" className="auth-font-heading text-[1.75rem] font-bold leading-[1.15] tracking-tight text-txt-primary md:text-[2.25rem] lg:text-4xl lg:leading-tight">
                {NEUTRAL_AUTH_HEADLINE}
              </h1>

              <p className="mt-4 max-w-md text-sm leading-relaxed text-txt-secondary lg:text-base">
                {NEUTRAL_AUTH_SUBHEAD}
              </p>
            </>
          )}

          {/* Benefits — more compact on phones/tablets; desktop density restored at lg. */}
          <ul className="mt-6 space-y-3 lg:mt-8 lg:space-y-4">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <li key={title} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#68B600]/25 bg-[#68B600]/10 text-[#68B600] lg:h-9 lg:w-9">
                  <Icon size={18} strokeWidth={1.75} />
                </span>
                <div>
                  <h2 className="auth-font-heading text-sm font-semibold text-txt-primary">{title}</h2>
                  {/* Slightly brighter than --txt-secondary, still clearly below the bold heading. */}
                  <p className="text-xs leading-relaxed text-txt-primary/80">{desc}</p>
                </div>
              </li>
            ))}
          </ul>

          <p className="mt-5 text-xs leading-relaxed text-txt-secondary lg:text-sm">
            Built for Indian traders automating TradingView, AmiBroker, Python and manual workflows.
          </p>
        </div>
      </section>

      </div>

      {/* Page footer — compact, non-fixed, visible on all sizes. On the AlgoSys portal the floating
          button is raised above this footer (see below) so it never covers the text; without that
          button the extra bottom clearance is not needed. */}
      <footer
        className={`relative z-10 px-6 pt-11 text-center lg:px-6 lg:py-4 ${
          presentation.showAlgosysMarketing
            ? 'pb-[calc(84px_+_env(safe-area-inset-bottom))]'
            : 'pb-[calc(28px_+_env(safe-area-inset-bottom))]'
        }`}
      >
        <p className="text-2xs text-txt-muted">{presentation.footerText}</p>
      </footer>

      {/* Floating WhatsApp enquiry button — AlgoSys's own enquiry channel, so it is rendered only on
          the resolved AlgoSys portal. Raised to 56px (mobile) so it clears the compact footer; 24px
          on desktop. Safe-area aware. */}
      {presentation.showAlgosysMarketing && (
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Enquire about AlgoSys on WhatsApp"
          className="fixed right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full border border-[#68B600]/40 bg-[#68B600] text-bg-base shadow-lg transition-colors hover:bg-[#68B600]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#68B600] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0b0d] bottom-[calc(56px_+_env(safe-area-inset-bottom))] lg:right-6 lg:bottom-[calc(24px_+_env(safe-area-inset-bottom))]"
        >
          <MessageCircle size={24} />
        </a>
      )}
    </main>
  );
}
